import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addCartLine, clearCart, deleteCartLine, getCart, updateCartLine, ApiError } from '@dss/api-client'
import { useAccessToken } from '@dss/auth'
import { api } from '../../lib/api'
import { useCartIdentity } from './usePortalCartContext'

export const cartQueryKey = (point: number | null, identity: string) =>
  ['portal-cart', identity, point] as const

export function useCart(point: number | null) {
  const token = useAccessToken()
  const identity = useCartIdentity()
  const query = useQuery({
    queryKey: cartQueryKey(point, identity),
    queryFn: ({ signal }) => getCart(api, token!, point!, signal),
    enabled: Boolean(token) && point != null, retry: false, staleTime: 0,
    refetchOnWindowFocus: 'always',
  })
  return { ...query, data: query.isError || point == null ? undefined : query.data }
}
function useCartMutation<T>(point: number | null,
  operation: (token: string, point: number, input: T) => Promise<unknown>) {
  const token = useAccessToken()
  const identity = useCartIdentity()
  const client = useQueryClient()
  return useMutation({
    scope: { id: `portal-cart:${identity}:${point}` },
    mutationFn: (input: T) => {
      if (!token || point == null) throw new Error('Сначала выберите торговую точку')
      let pending = false
      try { pending = sessionStorage.getItem(`portal-checkout:${identity}:${point}`) != null }
      catch { /* Проверка текущего runtime-lock ниже. */ }
      if (pending || client.getQueryData(['portal-cart-lock', identity]) === true) {
        throw new Error('Сначала завершите проверку заказа в корзине')
      }
      return operation(token, point, input)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: cartQueryKey(point, identity) }),
    onError: (error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        client.removeQueries({ queryKey: cartQueryKey(point, identity) })
        void client.invalidateQueries({ queryKey: ['portal-access', identity] })
      }
    },
  })
}
export function useAddToCart(point: number | null) {
  return useCartMutation(point, (token, current,
    input: { tradepointId: number; skuId: number; quantity: number | string }) => {
    if (input.tradepointId !== current) throw new Error('Торговая точка изменилась')
    return addCartLine(api, token, input)
  })
}
export function useUpdateCartLine(point: number | null) {
  return useCartMutation(point, (token, current,
    input: { skuId: number; quantity: string }) =>
    updateCartLine(api, token, current, input.skuId, { quantity: input.quantity }))
}
export function useDeleteCartLine(point: number | null) {
  return useCartMutation(point, (token, current, skuId: number) =>
    deleteCartLine(api, token, current, skuId))
}
export function useClearCart(point: number | null) {
  return useCartMutation(point, (token, current, _input: void) => clearCart(api, token, current))
}
